import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  primary_color: '#002FA7',
  sidebar_color: '#FFFFFF',
  navbar_color: '#FFFFFF',
  accent_color: '#FF6B00',
  theme: 'light',
  welcome_message: 'Welcome back',
  logo_url: null,
  company_name: 'CivilX TMS',
};

// Per-user theme override key. When set, this wins over the company branding's
// `theme` so each user can pick their own preference without flipping the
// company-wide setting for everybody else.
const THEME_OVERRIDE_KEY = 'civilx.theme.override';

const readThemeOverride = () => {
  try {
    const v = localStorage.getItem(THEME_OVERRIDE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
};

const BrandingContext = createContext(null);

export const useBranding = () => {
  const ctx = useContext(BrandingContext);
  return ctx || { branding: DEFAULTS, loading: false, refresh: () => {}, theme: 'light', setTheme: () => {} };
};

const applyTheme = (b) => {
  if (typeof document === 'undefined') return;
  const r = document.documentElement;
  const isDark = b.theme === 'dark';

  // In dark mode, if the admin kept the default white sidebar/navbar, swap
  // them to a dark slate so the shell isn't an eye-searing white strip.
  // Custom non-white colors are preserved.
  const sidebar = b.sidebar_color || DEFAULTS.sidebar_color;
  const navbar = b.navbar_color || DEFAULTS.navbar_color;
  const isWhite = (c) => !c || c.toUpperCase() === '#FFFFFF' || c.toUpperCase() === '#FFF';

  r.style.setProperty('--brand-primary', b.primary_color || DEFAULTS.primary_color);
  r.style.setProperty('--brand-sidebar', isDark && isWhite(sidebar) ? '#0f172a' : sidebar);
  r.style.setProperty('--brand-navbar', isDark && isWhite(navbar) ? '#0f172a' : navbar);
  r.style.setProperty('--brand-accent', b.accent_color || DEFAULTS.accent_color);

  // Toggle the `dark` class on <html> so Tailwind's `darkMode: ["class"]`
  // kicks in AND our global App.css dark-mode overrides apply across every page.
  if (isDark) {
    r.classList.add('dark');
  } else {
    r.classList.remove('dark');
  }
};

export const BrandingProvider = ({ children }) => {
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [themeOverride, setThemeOverride] = useState(readThemeOverride());

  // Effective theme = user override if set, else company branding.
  const effectiveTheme = themeOverride || branding.theme || 'light';

  const refresh = useCallback(async () => {
    if (!user || user.role === 'super_admin') {
      const next = { ...DEFAULTS, theme: themeOverride || DEFAULTS.theme };
      setBranding(DEFAULTS);
      applyTheme(next);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/company/branding`, { withCredentials: true });
      const merged = { ...DEFAULTS, ...(r.data || {}) };
      setBranding(merged);
      applyTheme({ ...merged, theme: themeOverride || merged.theme });
    } catch {
      setBranding(DEFAULTS);
      applyTheme({ ...DEFAULTS, theme: themeOverride || DEFAULTS.theme });
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.role, themeOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Public setter — flips the theme immediately for the current user only.
  const setTheme = useCallback((next) => {
    const v = next === 'dark' ? 'dark' : 'light';
    try { localStorage.setItem(THEME_OVERRIDE_KEY, v); } catch { /* ignore quota / private mode */ }
    setThemeOverride(v);
    applyTheme({ ...branding, theme: v });
  }, [branding]);

  const value = useMemo(
    () => ({ branding, loading, refresh, theme: effectiveTheme, setTheme }),
    [branding, loading, refresh, effectiveTheme, setTheme],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};
